<template>
  <main class="main-content mt-0">
    <div
      class="page-header align-items-start min-vh-50 pt-7 pb-9"
      :style="{
        backgroundImage:
          'url(' + require('@/assets/img/tpwauction2024-min.jpg') + ')',
      }"
    >
      <span class="mask bg-gradient-dark opacity-6"></span>
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-7 text-center mx-auto">
            <h1 class="text-white mb-2 mt-5">
              Welcome to <br />
              The Perfect Auction
            </h1>
            <p class="text-lead text-white mb-0">
              <template v-if="isKiosk && selectedTableName">
                {{ selectedTableName }} &mdash; Tap your name to sign in.
              </template>
              <template v-else-if="isKiosk">
                Select a table and tap your name to sign in.
              </template>
              <template v-else>
                Search your name and sign in.
              </template>
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="container">
      <div class="row mt-lg-n10 mt-md-n11 mt-n10 justify-content-center">
        <div class="col-xl-6 col-lg-7 col-md-9 mx-auto">
          <div class="card mt-5">
            <div class="card-header pb-0 text-start">
              <h3 class="font-weight-bolder">Sign in</h3>
              <p class="mb-0">{{ isKiosk ? 'Quick sign in for table device.' : 'Sign in with your name.' }}</p>
            </div>

            <div class="card-body text-start">
              <Message v-if="errMsg" severity="error" :closable="false">{{ errMsg }}</Message>

              <!-- ======= KIOSK MODE: table → user list ======= -->
              <template v-if="isKiosk">
                <div v-if="!selectedTableId">
                  <label class="mb-2">Select table:</label>
                  <div class="table-grid">
                    <button
                      v-for="table in tables"
                      :key="table.id"
                      type="button"
                      class="btn btn-outline-primary table-btn"
                      @click="selectTable(table)"
                    >
                      {{ table.name || table.id }}
                    </button>
                  </div>
                  <p v-if="!tables.length" class="text-muted mb-0">No tables found.</p>
                </div>

                <div v-else-if="!selectedUser">
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <small class="text-muted d-block">Active table</small>
                      <strong>{{ selectedTableName }}</strong>
                    </div>
                  </div>
                  <label class="mb-2">Who is signing in?</label>
                  <div class="user-list">
                    <button
                      v-for="user in tableUsers"
                      :key="user.id"
                      type="button"
                      class="btn btn-light user-btn"
                      @click="selectUser(user)"
                    >
                      <span>{{ user.fullName }}</span>
                      <span v-if="user.userAdmin" class="badge bg-dark ms-2">Admin</span>
                    </button>
                  </div>
                  <p v-if="!tableUsers.length" class="text-muted mb-0">
                    No active users assigned to this table.
                  </p>
                </div>
              </template>

              <!-- ======= NORMAL MODE: search all users ======= -->
              <template v-else>
                <div v-if="!selectedUser">
                  <label class="mb-2">Search your name:</label>
                  <input
                    type="text"
                    v-model="searchQuery"
                    class="form-control mb-3"
                    placeholder="Type your name..."
                    autocomplete="off"
                  />
                  <div v-if="searchQuery.length >= 2" class="user-list">
                    <button
                      v-for="user in filteredUsers"
                      :key="user.id"
                      type="button"
                      class="btn btn-light user-btn"
                      @click="selectUser(user)"
                    >
                      <span>{{ user.fullName }}</span>
                      <span v-if="user.assignedTableName" class="badge bg-secondary ms-2">{{ user.assignedTableName }}</span>
                    </button>
                  </div>
                  <p v-if="searchQuery.length >= 2 && !filteredUsers.length" class="text-muted mb-0">
                    No user found.
                  </p>
                  <p v-if="searchQuery.length < 2 && searchQuery.length > 0" class="text-muted mb-0">
                    Type at least 2 characters to search.
                  </p>
                </div>
              </template>

              <!-- ======= SHARED: user confirmation + login ======= -->
              <div v-if="selectedUser">
                <p class="mb-1">Signing in as:</p>
                <h5 class="mb-3">{{ selectedUser.fullName }}</h5>

                <div v-if="needsPassword" class="mb-3">
                  <label>{{ selectedUser?.password ? 'Enter password:' : 'Create a password:' }}</label>
                  <ArgonInput
                    type="password"
                    v-model="password"
                    class="password-input"
                    :placeholder="selectedUser?.password ? 'Password' : 'Choose a password'"
                    aria-label="Password"
                  />
                </div>

                <div v-if="needsMobile" class="mb-3">
                  <label>Mobile number (required):</label>
                  <ArgonInput
                    v-model="mobileNumber"
                    class="password-input"
                    placeholder="Ex. +46701234567"
                    aria-label="Mobile number"
                  />
                  <p class="text-sm text-muted mt-1 mb-0">
                    A mobile number is required to sign in.
                  </p>
                </div>

                <div class="d-flex flex-wrap gap-2">
                  <ArgonButton color="light" class="mb-0" @click="selectedUser = null">
                    Choose another person
                  </ArgonButton>

                  <ArgonButton
                    color="tpwf"
                    class="mb-0"
                    @click="saveMobileAndLogin"
                    :disabled="signingIn || (needsMobile && !mobileIsValid)"
                  >
                    Sign in
                  </ArgonButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  <!-- Mobile number popup for kiosk users without a number -->
  <Teleport to="body">
    <div v-if="showMobilePopup" class="mobile-popup-overlay">
      <div class="card mobile-popup-card">
        <div class="card-header pb-0 text-start">
          <h5 class="font-weight-bolder mb-1">Add phone number</h5>
          <p class="text-sm text-muted mb-0">Required to continue</p>
        </div>
        <div class="card-body text-start">
          <ArgonInput
            v-model="mobileNumber"
            placeholder="Ex. 0701234567"
            aria-label="Mobile number"
            autofocus
          />
          <p v-if="mobilePreview" class="text-sm text-muted mt-1 mb-0">
            Saves as: <strong>{{ mobilePreview }}</strong>
          </p>
          <p v-if="mobilePopupError" class="text-sm text-danger mt-1 mb-0">
            {{ mobilePopupError }}
          </p>
          <div class="d-flex gap-2 mt-3">
            <ArgonButton
              color="tpwf"
              class="mb-0"
              @click="saveMobilePopup"
              :disabled="!mobileIsValid"
            >
              Save
            </ArgonButton>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
  </main>
  <app-footer />
</template>

<script setup>
import { onBeforeMount, onBeforeUnmount, ref, computed } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { db } from "@/firebase";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  where,
  query,
} from "firebase/firestore";
import AppFooter from "@/views/PageLayout/Footer.vue";
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonButton from "@/components/ArgonButton.vue";

const router = useRouter();
const route = useRoute();
const store = useStore();

const redirectUrl = route.query.redirect || "/auction/category/All";

const tables = ref([]);
const tableUsers = ref([]);
const selectedTableId = ref(localStorage.getItem("deviceTableId") || "");
const selectedTableName = ref(localStorage.getItem("deviceTableName") || "");

// Kiosk mode: locked to a table, no table switching allowed
const isKiosk = ref(localStorage.getItem("deviceKiosk") === "true");

const allUsers = ref([]);
const searchQuery = ref("");
const selectedUser = ref(null);
const mobileNumber = ref("");
const password = ref("");
const errMsg = ref("");
const signingIn = ref(false);
const showMobilePopup = ref(false);
const mobilePopupError = ref("");

const filteredUsers = computed(() => {
  const q = searchQuery.value.toLowerCase().trim();
  if (q.length < 2) return [];
  return allUsers.value.filter((user) => user.fullName.toLowerCase().includes(q));
});

const needsMobile = computed(
  () => !!selectedUser.value && !selectedUser.value.mobile
);
const needsPassword = computed(
  () =>
    !!selectedUser.value &&
    (!!selectedUser.value.userAdmin || (store.state.requirePassword && !isKiosk.value))
);

const normalizeTable = (value) =>
  String(value || "")
    .toLowerCase()
    .replace("table", "")
    .replace("bord", "")
    .replace(/\s+/g, "");

const formatSwedishPhone = (phone) => {
  // Strip whitespace, dashes, dots and parentheses
  let cleaned = String(phone || "").replace(/[\s\-.()]/g, "");
  // "00" international prefix → "+"
  if (cleaned.startsWith("00")) {
    cleaned = "+" + cleaned.substring(2);
  }
  // Local Swedish number starting with "0" → prepend "+46" and drop the 0
  if (cleaned.startsWith("0")) {
    cleaned = "+46" + cleaned.substring(1);
  }
  // Trunk-0 bug: user typed country code AND local trunk 0
  // (e.g. "+46 0701234567" → "+460701234567"). Strip the extra 0(s).
  // This caused numbers like "+4604747243036" in the wild.
  cleaned = cleaned.replace(/^\+460+/, "+46");
  return cleaned;
};

// A valid mobile number is one that, after formatting, begins with a country
// code ("+" followed by 7–14 more digits — E.164 max is 15).
// Swedish mobile numbers must start with "+467" (mobile prefix 07x).
const isValidMobile = (raw) => {
  const formatted = formatSwedishPhone(String(raw || "").trim());
  if (!/^\+\d{7,15}$/.test(formatted)) return false;
  // Swedish-specific check: +46 followed by anything other than 7 (mobile) is
  // almost certainly a landline or malformed — reject so Twilio doesn't eat it.
  if (formatted.startsWith("+46") && !formatted.startsWith("+467")) return false;
  return true;
};

const mobileIsValid = computed(() => isValidMobile(mobileNumber.value));

const formatName = (user) => {
  const first = user.firstName || "";
  const last = user.lastName || "";
  const fullName = `${first} ${last}`.trim();
  return fullName || user.name || "Ok\u00E4nd anv\u00E4ndare";
};

const fetchAllUsers = async () => {
  const usersSnapshot = await getDocs(collection(db, "users"));
  allUsers.value = usersSnapshot.docs
    .map((userDoc) => ({
      id: userDoc.id,
      ...userDoc.data(),
      fullName: formatName(userDoc.data()),
    }))
    .filter((user) => user.userEnabled !== false)
    .sort((a, b) => a.fullName.localeCompare(b.fullName));
};

const fetchTables = async () => {
  const tableSnapshot = await getDocs(collection(db, "tables"));
  tables.value = tableSnapshot.docs
    .map((tableDoc) => ({
      id: tableDoc.id,
      ...tableDoc.data(),
    }))
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), undefined, { numeric: true }));
};

const fetchUsersForTable = async (tableId) => {
  const usersQuery = query(collection(db, "users"), where("assignedTable", "==", tableId));
  const usersSnapshot = await getDocs(usersQuery);

  tableUsers.value = usersSnapshot.docs
    .map((userDoc) => ({
      id: userDoc.id,
      ...userDoc.data(),
      fullName: formatName(userDoc.data()),
    }))
    .filter((user) => user.userEnabled !== false)
    .sort((a, b) => a.fullName.localeCompare(b.fullName));
};

const selectTable = async (table) => {
  errMsg.value = "";
  selectedUser.value = null;
  selectedTableId.value = table.id;
  selectedTableName.value = table.name || table.id;
  localStorage.setItem("deviceTableId", selectedTableId.value);
  localStorage.setItem("deviceTableName", selectedTableName.value);
  await fetchUsersForTable(selectedTableId.value);
};

const resolveTableFromQuery = async () => {
  if (!tables.value.length) return;

  // Reset kiosk/table assignment via ?reset=true
  if (route.query.reset === "true") {
    localStorage.removeItem("deviceTableId");
    localStorage.removeItem("deviceTableName");
    localStorage.removeItem("deviceKiosk");
    selectedTableId.value = "";
    selectedTableName.value = "";
    isKiosk.value = false;
    await fetchAllUsers();
    return;
  }

  const queryTableId = route.query.tableId;
  const queryTableName = route.query.table;

  // Persist kiosk flag from URL
  if (route.query.kiosk === "true") {
    isKiosk.value = true;
    localStorage.setItem("deviceKiosk", "true");
  }

  let matchedTable = null;

  if (queryTableId) {
    matchedTable = tables.value.find((table) => table.id === queryTableId);
  }

  if (!matchedTable && queryTableName) {
    const normalized = normalizeTable(queryTableName);
    matchedTable = tables.value.find(
      (table) =>
        normalizeTable(table.id) === normalized ||
        normalizeTable(table.name) === normalized
    );
  }

  if (matchedTable) {
    await selectTable(matchedTable);
    return;
  }

  if (selectedTableId.value) {
    const storedTable = tables.value.find((table) => table.id === selectedTableId.value);
    if (storedTable) {
      selectedTableName.value = storedTable.name || storedTable.id;
      await fetchUsersForTable(storedTable.id);
      return;
    }
  }

  selectedTableId.value = "";
  selectedTableName.value = "";
};

const selectUser = async (user) => {
  errMsg.value = "";
  selectedUser.value = user;
  mobileNumber.value = user.mobile || "";
  password.value = "";

  // Kiosk mode with a table locked to this device: instant login for non-admin users, no password
  if (isKiosk.value && !!selectedTableId.value && !user.userAdmin) {
    await login();
    return;
  }

  // Kiosk mode without a fixed table: instant login for non-admin users (mobile handled by popup)
  if (isKiosk.value && !user.userAdmin) {
    await login();
  }
};

const login = async () => {
  if (!selectedUser.value) {
    errMsg.value = "Please select a user.";
    return;
  }

  if (needsPassword.value && !password.value) {
    errMsg.value = "Please enter your password.";
    return;
  }

  try {
    signingIn.value = true;
    errMsg.value = "";

    await store.dispatch("customLogInByUserId", {
      userId: selectedUser.value.id,
      password: password.value,
      bypassPassword: isKiosk.value,
    });

    if (isKiosk.value && !selectedUser.value.mobile) {
      showMobilePopup.value = true;
    } else {
      router.push(redirectUrl);
    }
  } catch (error) {
    errMsg.value = error?.message || "Could not sign in.";
  } finally {
    signingIn.value = false;
  }
};

const saveMobileAndLogin = async () => {
  if (!selectedUser.value) return;

  // If the user doesn't have a mobile number on file, they must provide one
  // before they can sign in.
  if (needsMobile.value) {
    if (!isValidMobile(mobileNumber.value)) {
      errMsg.value =
        "A valid mobile number is required to sign in (include country code, e.g. +46701234567).";
      return;
    }
    const mobile = formatSwedishPhone(mobileNumber.value.trim());
    await updateDoc(doc(db, "users", selectedUser.value.id), {
      mobile,
    });
    selectedUser.value.mobile = mobile;
  }

  await login();
};


const mobilePreview = computed(() => {
  const raw = mobileNumber.value.trim();
  if (!raw) return "";
  const formatted = formatSwedishPhone(raw);
  return formatted !== raw ? formatted : "";
});

const saveMobilePopup = async () => {
  if (!isValidMobile(mobileNumber.value)) {
    mobilePopupError.value =
      "A valid mobile number is required (include country code, e.g. +46701234567).";
    return;
  }
  mobilePopupError.value = "";
  const mobile = formatSwedishPhone(mobileNumber.value.trim());
  if (selectedUser.value) {
    await updateDoc(doc(db, "users", selectedUser.value.id), { mobile });
    selectedUser.value.mobile = mobile;
  }
  showMobilePopup.value = false;
  router.push(redirectUrl);
};

const toggleDefaultLayout = () => store.commit("toggleDefaultLayout");

onBeforeMount(async () => {
  store.state.hideConfigButton = true;
  toggleDefaultLayout();
  document.body.classList.remove("bg-gray-100");

  // Detect kiosk from URL before deciding flow
  if (route.query.kiosk === "true") {
    isKiosk.value = true;
    localStorage.setItem("deviceKiosk", "true");
  }

  if (isKiosk.value) {
    await fetchTables();
    await resolveTableFromQuery();
  } else {
    await fetchAllUsers();
  }
});

onBeforeUnmount(() => {
  store.state.hideConfigButton = false;
  toggleDefaultLayout();
  document.body.classList.add("bg-gray-100");
});
</script>

<style scoped>
.table-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 0.5rem;
}

.table-btn {
  text-align: left;
}

.user-list {
  display: grid;
  gap: 0.5rem;
}

.user-btn {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.gap-2 {
  gap: 0.5rem;
}

.mobile-popup-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.mobile-popup-card {
  width: 100%;
  max-width: 400px;
  margin: 1rem;
}
</style>
